import { logWarn } from "@/helpers/logError";

// LSP-20530: a shared-project URL is `/projects/<projectId>?shareId=<shareId>` and EntityDetailPage
// appends the active tab as a hash (`#documents`, `#about`, `#media`). The previous string-splitting
// parse (`url.split("shareId=")[1].split("&")[0]`) kept that hash glued to the share id, so a link that
// was reloaded or copied from the address bar on a tab was sent to the API as `shareId=<uuid>#documents`
// and the shared project rendered as "Sorry, we couldn't find that project." Parse with the URL API,
// which separates the query string from the fragment.

export interface ShareLinkLocation {
	projectId: string;
	shareId: string;
}

// Placeholder origin so a router `fullPath` (which has no origin) parses like a full href.
const PATH_ONLY_BASE = "https://share-link.invalid";

export function parseShareLinkLocation(urlOrPath: string): ShareLinkLocation {
	let url: URL;
	try {
		url = new URL(urlOrPath, PATH_ONLY_BASE);
	} catch {
		return { projectId: "", shareId: "" };
	}
	const projectMatch = url.pathname.match(/\/projects2?\/([^/]+)/);
	return {
		projectId: projectMatch?.[1] ?? "",
		shareId: url.searchParams.get("shareId") ?? ""
	};
}

// LSP-20890: `/projects/<projectId>/<shareId>` is an alias of the canonical `?shareId=` share link. The
// router redirects it, but that redirect only reaches the address bar once the initial navigation
// settles — and the signed-out share-link guard awaits getProjectInsecure first. App.vue decides from
// window.location at boot whether to mount the public project view, so it saw the path form and left
// the recipient on LoadingPage. Returns the canonical path + query + hash, or null when the location is
// not the path form. Vue Router matches paths case-insensitively and tolerates one trailing slash, so
// this does too. The path segment wins over a stray `?shareId=` because it is the link's own token.
const PATH_FORM_SHARE_LINK = /^\/projects\/([^/]+)\/([^/]+)\/?$/i;

export interface PathFormShareLink extends ShareLinkLocation {
	/** The share-id path segment exactly as it appeared in the URL, before any decoding. */
	rawShareIdSegment: string;
	/** False when the segment is not valid percent-encoding and `shareId` is the raw segment. */
	shareIdDecoded: boolean;
}

// LSP-21122: the one path-form matcher, shared by the pre-boot rewrite below and App.vue's boot gate
// (which must still recognise the path form when that rewrite was skipped). A segment that is not
// valid percent-encoding keeps its raw text as the share id — exactly what Vue Router's param
// decode() does — so App and the router guard look the project up with the same id.
export function parsePathFormShareLink(pathname: string): PathFormShareLink | null {
	const match = pathname.match(PATH_FORM_SHARE_LINK);
	if (!match) {
		return null;
	}
	const rawShareIdSegment = match[2];
	try {
		return { projectId: match[1], shareId: decodeURIComponent(rawShareIdSegment), rawShareIdSegment, shareIdDecoded: true };
	} catch {
		return { projectId: match[1], shareId: rawShareIdSegment, rawShareIdSegment, shareIdDecoded: false };
	}
}

// LSP-21122: log-safe description of a share-id path segment. A malformed segment can still carry a
// usable token (a UUID with a stray `%` appended), so logs get its length and the percent-escape
// sequences that decide whether it decodes — never the segment itself. The Datadog event's own
// view.url already records the page path for anyone who needs the full link.
export function describeShareIdSegmentForLog(rawShareIdSegment: string): {
	shareIdSegmentLength: number;
	percentEscapeSequences: string[];
} {
	return {
		shareIdSegmentLength: rawShareIdSegment.length,
		percentEscapeSequences: rawShareIdSegment.match(/%[0-9A-Fa-f]{0,2}/g) ?? []
	};
}

export function canonicalizePathFormShareLink(location: Pick<Location, "pathname" | "search" | "hash">): string | null {
	const pathForm = parsePathFormShareLink(location.pathname);
	if (!pathForm) {
		return null;
	}
	if (!pathForm.shareIdDecoded) {
		// LSP-21122: declining used to be silent. App.vue's boot gate now mounts the public view from the
		// path form itself, and the lookup fails to "couldn't find that project" — log why no rewrite ran.
		logWarn("shareLink: path-form share link has a share id that is not valid percent-encoding; not rewriting it before boot", {
			projectId: pathForm.projectId,
			...describeShareIdSegmentForLog(pathForm.rawShareIdSegment)
		});
		return null;
	}
	const query = new URLSearchParams(location.search);
	query.set("shareId", pathForm.shareId);
	return `/projects/${pathForm.projectId}?${query.toString()}${location.hash}`;
}
